import { ReactElement, useState } from 'react';
import { VariableSizeList as List } from 'react-window';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import CollapsibleList from 'lib/CollapsibleList';
import Tooltip, { TooltipDirection } from 'lib/Tooltip';
import { useAvailableHeight } from 'customHooks/useAvailableHeight';
import { SortDir } from 'components/common/types/SortDir.types';
import { getFilenameParts } from 'utils/fileUtils';
import { UploadedFilesListProps } from './UploadedFilesList.types';

const EXTRA_HEIGHT = 20;

const renderFilename = (file: string): ReactElement => {
  const { filename, extension } = getFilenameParts(file);
  const isFilenameTooLong = filename.trim().length >= 16;

  return (
    <Tooltip tooltip={isFilenameTooLong ? file : undefined} direction={TooltipDirection.BOTTOM}>
      <span className="text-neutral-900 flex w-[100px]">
        {isFilenameTooLong ? (
          <>
            <span className="block w-[100px] whitespace-nowrap overflow-hidden text-ellipsis">{filename}</span>
            <span className="block">.{extension}</span>
          </>
        ) : (
          <span className="block w-[100px] whitespace-nowrap overflow-hidden text-ellipsis">{file}</span>
        )}
      </span>
    </Tooltip>
  );
};

const UploadedFilesList = ({
  uploadedFiles,
  openCategories,
  onToggleCategory,
  onSort,
  sortDirection,
  itemHeight,
  renderItem,
  renderHeaderComponent,
}: UploadedFilesListProps): React.ReactElement => {
  const [onHoverCategory, setOnHoverCategory] = useState<string>('');

  const { setHeight, getHeight, setRefElement } = useAvailableHeight({
    itemHeight,
    minHeight: itemHeight,
  });

  const handleToggleCategory = (name: string, itemCount: number): void => {
    setHeight(name, itemCount);
    onToggleCategory(name);
  };

  return (
    <>
      {uploadedFiles.map((item, index) => {
        if (!item?.fileContents?.length) return null;

        const isOpen = openCategories.includes(item.name);
        const fileItemsCount = item.fileContents.length;
        const availableExpandedHeight = getHeight(item.name);

        return (
          <span
            key={`uploaded-file-container-${item.name}`}
            onMouseEnter={() => setOnHoverCategory(item.name)}
            onMouseLeave={() => setOnHoverCategory('')}
            ref={(element) => setRefElement(item.name, element as HTMLDivElement)}
          >
            <CollapsibleList
              label={renderFilename(item.name)}
              isSortVisible
              onSort={(direction: SortDir) => onSort(item.name, direction)}
              sortDir={sortDirection}
              isOpen={isOpen}
              onToggle={() => handleToggleCategory(item.name, fileItemsCount)}
              headerComponent={renderHeaderComponent(item, index, onHoverCategory === item.name)}
              customExpandedHeight={availableExpandedHeight ? availableExpandedHeight + EXTRA_HEIGHT : undefined}
            >
              {isOpen && fileItemsCount > 0 && (
                <div className="flex w-full overflow-hidden" style={{ height: availableExpandedHeight }}>
                  <AutoSizer>
                    {({ width, height }: Size) => (
                      <List
                        ref={(ref) => ref?.resetAfterIndex(0)}
                        height={height}
                        width={width}
                        itemCount={fileItemsCount}
                        itemSize={() => itemHeight}
                        overscanCount={5}
                      >
                        {(props) => renderItem(item.fileContents![props.index], index, props.style)}
                      </List>
                    )}
                  </AutoSizer>
                </div>
              )}
            </CollapsibleList>
          </span>
        );
      })}
    </>
  );
};

export default UploadedFilesList;
